import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, Loader2, AlertCircle, ArrowRight, 
  Clock, RefreshCw, CreditCard, Star
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    const poll = async (attempt) => {
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        toast.error('Payment status check timed out. Please check your email for confirmation.');
        return;
      }

      try {
        const response = await fetch(`${API}/payments/checkout/status/${sessionId}`);
        const data = await response.json();

        if (response.ok) {
          setPaymentData(data);

          if (data.payment_status === 'paid') {
            setStatus('success');
            toast.success('Subscription confirmed! Credits allocated.');
            return;
          } else if (data.status === 'expired') {
            setStatus('expired');
            toast.error('Payment session expired.');
            return;
          }

          setPollCount(attempt + 1);
          setTimeout(() => poll(attempt + 1), pollInterval);
        } else {
          setStatus('error');
          toast.error(data.detail || 'Failed to check payment status');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
        toast.error('Error checking payment status');
      }
    };

    poll(0);
  };

  useEffect(() => {
    if (!sessionId) {
      navigate('/subscribe');
      return;
    }
    pollPaymentStatus();
  }, [sessionId, navigate]); // eslint-disable-line

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center py-12">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#b8860b] animate-spin" />
            <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment...</h2>
            <p className="text-gray-400">Please wait while we confirm your subscription</p>
            <p className="text-sm text-gray-500 mt-4">Attempt {pollCount + 1} of 10</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Subscription Activated!</h2>
            <p className="text-gray-400 mb-8">Your platform credits have been allocated</p>

            <Card className="bg-[#1a2d4a]/50 border-[#b8860b]/30 max-w-md mx-auto">
              <CardContent className="py-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Star className="w-5 h-5 text-[#b8860b]" />
                    <span className="text-white font-bold text-lg">
                      {paymentData?.tier_name} Plan
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">BETH Credits</span>
                    <span className="text-white font-bold text-xl">
                      {paymentData?.beth_credits?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Amount Paid</span>
                    <span className="text-[#b8860b] font-semibold">
                      ${paymentData?.usd_amount?.toLocaleString()}
                    </span>
                  </div>

                  {paymentData?.credits_allocated ? (
                    <div className="flex items-center gap-2 text-green-400 bg-green-500/10 px-3 py-2 rounded-lg">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Credits allocated to your account</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-yellow-400 bg-yellow-500/10 px-3 py-2 rounded-lg">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">Processing allocation...</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#b8860b] hover:bg-[#9a7209]"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/credit-enhancement')}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-[#1a2d4a]"
              >
                Start Using Tools
              </Button>
            </div>

            <p className="mt-6 text-sm text-gray-500">
              Your credits will be debited as you use platform features. 
              Renew monthly to maintain access.
            </p>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-12 h-12 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Session Expired</h2>
            <p className="text-gray-400 mb-8">Your payment session has expired. Please try again.</p>
            <Button
              onClick={() => navigate('/subscribe')}
              className="bg-[#b8860b] hover:bg-[#9a7209]"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        );

      case 'timeout':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <AlertCircle className="w-12 h-12 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Status Check Timeout</h2>
            <p className="text-gray-400 mb-4">
              We could not confirm your payment status. If payment was completed, 
              your credits will still be allocated.
            </p>
            <p className="text-sm text-gray-500 mb-8">
              Please check your email for confirmation or contact support.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => {
                  setStatus('checking');
                  setPollCount(0);
                  pollPaymentStatus();
                }}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-[#1a2d4a]"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry Check
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#b8860b] hover:bg-[#9a7209]"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Error</h2>
            <p className="text-gray-400 mb-8">
              There was an error checking your payment status. Please contact support.
            </p>
            <Button
              onClick={() => navigate('/subscribe')}
              className="bg-[#b8860b] hover:bg-[#9a7209]"
            >
              Back to Subscribe
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#1a2d4a] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="bg-[#0d1f3c] border-[#b8860b]/30">
          <CardContent className="p-8">
            {renderContent()}
          </CardContent>
        </Card>

        {status === 'success' && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Transaction ID: {sessionId}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
